import { useEffect, useRef, useState } from 'react';
import { callApi } from '../lib/api.ts';
import { friendlyError } from '../lib/friendlyError.ts';
import type { TokenSchema } from '../lib/tokenSchema.ts';

/** The committed brand-kit page lives at this sub-path (public/brand-kit.html), not
 *  the deploy root — which is a blank canvas at the brand stage. */
const PAGE_PATH = '/brand-kit.html';

/**
 * "Preview brand kit" (§15a): generate a polished, self-contained brand-kit web
 * page from the current tokens and surface it as a real clickable link — the
 * "see it beautifully" confirmable surface. Generates the page (server commits it),
 * then polls the Cloudflare deploy until the link is live. The link is DURABLE:
 * on load it re-resolves from the latest successful brand-preview build, so it
 * survives reload/navigation (it's the shareable confirm link — the heart of the step).
 */
export function BrandPreview({
  clientId,
  tokens,
  disabled,
  latestPreviewBuildId,
}: {
  clientId: string;
  tokens: TokenSchema;
  disabled: boolean;
  /** Most-recent SUCCESSFUL brand-preview build id (newest look), or null. */
  latestPreviewBuildId?: string | null;
}) {
  const [phase, setPhase] = useState<string | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const generatingRef = useRef(false); // guard against rapid double-clicks piling up builds

  // Durable link: resolve the latest successful brand-preview deploy on load (and when
  // a new one lands) so the confirm link is always present — not just right after a
  // generate. A live generate (phase set) owns the link; don't fight it.
  useEffect(() => {
    if (!latestPreviewBuildId) {
      setLink(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      const started = Date.now();
      for (;;) {
        if (cancelled || phase) return; // a fresh generate is driving the link
        let s: { status: string; url: string | null };
        try {
          s = await callApi<{ status: string; url: string | null }>('build-status', {
            method: 'POST',
            body: JSON.stringify({ buildId: latestPreviewBuildId }),
          });
        } catch {
          return;
        }
        if (cancelled) return;
        if (s.url && (s.status === 'ready' || s.status === 'unconfigured')) {
          setLink(`${s.url.replace(/\/$/, '')}${PAGE_PATH}`);
          return;
        }
        if (s.status === 'failed' || s.status === 'no_project') return;
        if (Date.now() - started > 3 * 60_000) return;
        await new Promise((r) => setTimeout(r, 5_000));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestPreviewBuildId]);

  async function preview() {
    if (generatingRef.current) return; // a generate is already in flight
    generatingRef.current = true;
    setErr(null);
    setLink(null);
    setPhase('Generating the page…');
    try {
      const { buildId, pagePath } = await callApi<{ buildId: string; pagePath: string }>('brand-preview', {
        method: 'POST',
        body: JSON.stringify({ clientId, tokens }),
      });
      setPhase('Publishing the preview…');
      const started = Date.now();
      let fails = 0;
      for (;;) {
        await new Promise((r) => setTimeout(r, 4_000));
        let s: { status: string; url: string | null };
        try {
          s = await callApi<{ status: string; url: string | null }>('build-status', {
            method: 'POST',
            body: JSON.stringify({ buildId }),
          });
          fails = 0;
        } catch (pollErr) {
          // Tolerate transient blips, but STOP after a few so we never hammer a
          // failing endpoint (the 502 storm).
          if (++fails >= 3) throw pollErr;
          await new Promise((r) => setTimeout(r, 2_000 * fails)); // back off
          continue;
        }
        if (s.url && (s.status === 'ready' || s.status === 'unconfigured')) {
          setLink(`${s.url.replace(/\/$/, '')}${pagePath}`);
          setPhase(null);
          break;
        }
        if (s.status === 'failed' || s.status === 'no_project') {
          throw new Error(s.status === 'no_project' ? 'no preview project configured' : 'the preview build failed');
        }
        if (Date.now() - started > 5 * 60_000) throw new Error('the preview took too long');
      }
    } catch (e) {
      console.error('[brand-preview]', e);
      setErr(friendlyError(e, 'Couldn’t generate the preview — please try again.'));
      setPhase(null);
    } finally {
      generatingRef.current = false;
    }
  }

  return (
    // DS-sweep (2-5): this whole "Preview brand kit" CTA is a cohesive ON-DARK card
    // (bg-gray-900 + on-dark light text tints gray-300 / amber-200 / red-300 + a white
    // button). The 13 neutrals are hex-var (no /opacity) and there's no on-dark token
    // set, so it's left raw as one unit — same call as BuildChat's steering-wheel bar
    // (2-3) and MediaStructuring's selection toolbar (2-4a), for the future on-dark-chrome
    // structural pass. Partially tokenising just the frame would split a cohesive cluster.
    <div className="space-y-2 rounded border bg-gray-900 p-4 text-white">
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={preview}
          disabled={disabled || !!phase}
          className="rounded bg-white px-4 py-2 text-sm font-semibold text-gray-900 disabled:opacity-50"
        >
          {phase ? 'Working…' : 'Preview brand kit'}
        </button>
        <span className="text-xs text-gray-300">
          See the whole design system on a real page — share it to confirm before building.
        </span>
      </div>
      {phase && <p className="text-xs text-amber-200">{phase} (~1–2 min)</p>}
      {link && (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block rounded bg-emerald-500 px-4 py-2 text-sm font-semibold text-white"
        >
          Open the brand-kit page ↗
        </a>
      )}
      {err && <p className="text-xs text-red-300">{err}</p>}
    </div>
  );
}
